import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

import DnaBackground from '../components/DnaBackground';
import ResultsBackgroundElements from '../components/ResultsBackgroundElements';
import FloatingDnaMarkers from '../components/FloatingDnaMarkers';
import FounderTypeCard from '../components/FounderTypeCard';
import ShareResults from '../components/ShareResults';
import DeepDiveSection from '../components/DeepDiveSection';
import MeetingScriptsSection from '../components/MeetingScriptsSection';
import PremiumContent from '../components/PremiumContent';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import TypeIdentityHero from '../components/TypeIdentityHero';
import PremiumWelcomeBanner from '../components/PremiumWelcomeBanner';
import ResultsTabNav, { ResultsTabPanel } from '../components/ResultsTabNav';
import ResultsToolsGrid from '../components/ResultsToolsGrid';
import ResultDriversPanel from '../components/ResultDriversPanel';
import Seo from '../components/Seo';
import StorageBlockedNotice from '../components/StorageBlockedNotice';
import CofounderCompareTeaser from '../components/CofounderCompareTeaser';
import ResultsEmailSection, { validateEmail } from '../components/results/ResultsEmailSection';
import ResultsHistorySection from '../components/results/ResultsHistorySection';
import ResultsBackupSection from '../components/results/ResultsBackupSection';
import useResultsProfile from '../hooks/useResultsProfile';

import { founderTypes } from '../data/founderTypes';
import { formatFounderTitle } from '../utils/textUtils';
import { typePlaybooks } from '../data/typePlaybooks';
import { addEmailCapture, getEmailCaptures } from '../utils/emailCaptureStorage';
import { submitEmailReport } from '../utils/emailService';
import { trackEvent } from '../utils/analytics';
import { downloadWeeklyReminderIcs, getNextWeeklyDate } from '../utils/calendarUtils';
import { encodeResultsShareParam } from '../utils/shareLinkUtils';
import {
  buildPrivateBackup,
  downloadPrivateBackup,
  getBackupSummary,
  parsePrivateBackup,
  restorePrivateBackup,
  clearAllLocalUserData,
} from '../utils/backupStorage';
import { isLocalStorageAvailable } from '../utils/storageAvailability';
import { RESULTS } from '../data/productCopy';
import { buildTypeCode } from '../utils/founderIdentity';
import {
  ResultsContainer,
  ResultsContent,
  NoResultsMessage,
  RetakeButton,
  ScoreCardsContainer,
  DetailedCard,
  SectionTitle,
  ActionPlan,
  OperatingCard,
  List,
  OrderedList,
  SmallMeta,
  CheckInCard,
  CheckInRow,
  CheckInMeta,
  CheckInButton,
  ReminderButton,
  ConfidenceCard,
  ArchetypeCard,
  CompatibilitySection,
  CompatibilityDescription,
  CompatibilityTitle,
  ProfileBreakdownSection,
  ProfileBreakdownTitle,
} from './results/resultsStyles';

const Results = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storageAvailable = isLocalStorageAvailable();
  const {
    profile,
    createdAt,
    latestCheckIn,
    historyRuns,
    selectedRunId,
    handleViewRun,
    handleHistoryNameChange,
    handleHistoryNameCommit,
    refreshFromStorage,
  } = useResultsProfile();
  const [email, setEmail] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailDeliveryMode, setEmailDeliveryMode] = useState(null);
  const [emailConsent, setEmailConsent] = useState(false);
  const [hasAnyEmailCapture, setHasAnyEmailCapture] = useState(() => getEmailCaptures().length > 0);
  const [reminderDownloaded, setReminderDownloaded] = useState(false);
  const [backupError, setBackupError] = useState('');
  const [backupMessage, setBackupMessage] = useState('');
  const [pendingBackup, setPendingBackup] = useState(null);
  const [pendingBackupSummary, setPendingBackupSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    trackEvent('results_tab_changed', { tab });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleExportPdf = () => {
    const shareParam = new URLSearchParams(location.search).get('share');
    navigate(shareParam ? `/results/print?share=${shareParam}` : '/results/print');
  };

  const handleRetakeQuiz = () => {
    navigate('/');
  };

  const handleExportBackup = () => {
    setBackupError('');
    setBackupMessage('');
    try {
      const backup = buildPrivateBackup();
      downloadPrivateBackup(backup);
      const summary = getBackupSummary(backup);
      setBackupMessage(
        `Backup downloaded. Runs: ${summary.resultsHistoryCount}, check ins: ${summary.checkInsCount}.`
      );
      trackEvent('backup_exported', summary);
    } catch (e) {
      setBackupError('Could not export backup. Please try again.');
    }
  };

  const handleDeleteAllLocalData = () => {
    const confirmed = window.confirm(
      'Delete all FounderDNA data on this device? This removes your results, check-ins, email captures, and preferences. This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      clearAllLocalUserData();
      trackEvent('local_data_deleted');
      navigate('/', { replace: true });
    } catch (e) {
      setBackupError('Could not delete local data. Try clearing site data in your browser settings.');
    }
  };

  const handleChooseImportFile = () => {
    setBackupError('');
    setBackupMessage('');
    setPendingBackup(null);
    setPendingBackupSummary(null);
  };

  const handleImportFileSelected = async (event) => {
    setBackupError('');
    setBackupMessage('');

    const file = event.target.files && event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = parsePrivateBackup(text);
      const summary = getBackupSummary(parsed);
      setPendingBackup(parsed);
      setPendingBackupSummary(summary);
      setBackupMessage('Backup loaded. Review it, then restore.');
      trackEvent('backup_import_loaded', summary);
    } catch (e) {
      setBackupError('This backup file could not be read. Make sure it is a valid FounderDNA backup JSON.');
      setPendingBackup(null);
      setPendingBackupSummary(null);
    }
  };

  const handleRestoreBackup = () => {
    setBackupError('');
    setBackupMessage('');
    if (!pendingBackup) return;

    const summary = pendingBackupSummary || getBackupSummary(pendingBackup);
    const ok = window.confirm(
      `Restore this backup now?\n\nThis will merge local data with the backup. Runs: ${summary.resultsHistoryCount}, check ins: ${summary.checkInsCount}.`
    );
    if (!ok) return;

    try {
      restorePrivateBackup(pendingBackup, { mode: 'merge' });
      refreshFromStorage();
      setHasAnyEmailCapture(getEmailCaptures().length > 0);
      setPendingBackup(null);
      setPendingBackupSummary(null);
      setBackupMessage('Backup restored.');
      trackEvent('backup_restored', summary);
    } catch (e) {
      setBackupError('Restore failed. Your data was not changed.');
    }
  };
  
  // If no results are found, show a message to take the quiz
  if (!profile || !profile.primaryType) {
    return (
      <ResultsContainer id="main-content">
        <DnaBackground />
        <ResultsBackgroundElements />
        <SiteHeader />
        <ResultsContent>
          {!storageAvailable && <StorageBlockedNotice />}
          <NoResultsMessage>
            <h2>No results found</h2>
            <p>It looks like you haven't taken the FounderDNA test yet, or your results have been cleared.</p>
            <RetakeButton 
              onClick={handleRetakeQuiz}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Take the test
            </RetakeButton>
          </NoResultsMessage>
        </ResultsContent>
      </ResultsContainer>
    );
  }
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };
  
  // Get the primary founder type object
  const primaryFounderType = founderTypes[profile.primaryType];
  const secondaryFounderType = profile.secondaryType
    ? founderTypes[profile.secondaryType]
    : null;
  const playbook = typePlaybooks[profile.primaryType];

  const nextCheckInDate = latestCheckIn?.createdAt
    ? getNextWeeklyDate(latestCheckIn.createdAt)
    : createdAt
      ? getNextWeeklyDate(createdAt)
      : getNextWeeklyDate(new Date());

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/results?share=${encodeResultsShareParam(profile)}`
    : null;

  const handleDownloadReminder = () => {
    const start = nextCheckInDate;
    downloadWeeklyReminderIcs({
      title: 'FounderDNA weekly check in',
      description: 'A quick weekly check in. Answer 3 questions and pick one focus for the next 7 days.',
      startDate: start,
      durationMinutes: 15,
    });
    setReminderDownloaded(true);
    trackEvent('reminder_downloaded', { type: 'ics' });
  };

  const handleSaveEmail = async () => {
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      setEmailSaved(false);
      return;
    }

    if (!emailConsent) {
      setEmailError('Please confirm you agree to the Privacy Policy before we email your report.');
      setEmailSaved(false);
      return;
    }

    setEmailError('');
    setIsEmailSending(true);
    setEmailDeliveryMode(null);

    const trimmedEmail = email.trim();
    const printPath = shareUrl
      ? `/results/print?share=${new URLSearchParams(location.search).get('share')}`
      : '/results/print';
    const printUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${printPath}`
      : null;

    addEmailCapture({
      email: trimmedEmail,
      context: {
        source: 'results',
        primaryType: profile?.primaryType || null,
        secondaryType: profile?.secondaryType || null,
        createdAt: createdAt || null,
      },
    });

    try {
      await submitEmailReport({
        email: trimmedEmail,
        profile,
        typeCode: buildTypeCode(profile.primaryType, profile.secondaryType),
        primaryTitle: primaryFounderType ? formatFounderTitle(primaryFounderType.title) : null,
        secondaryTitle:
          profile?.secondaryType && founderTypes[profile.secondaryType]
            ? formatFounderTitle(founderTypes[profile.secondaryType].title)
            : null,
        stressFailureMode: playbook?.stressFailureMode || null,
        weekPlan: playbook?.threeStepWeekPlan || [],
        shareUrl,
        printUrl,
      });

      setEmailSaved(true);
      setEmailDeliveryMode('sent');
      setHasAnyEmailCapture(true);
      trackEvent('email_report_sent', {
        source: 'results',
        primaryType: profile?.primaryType || null,
      });
    } catch (error) {
      console.warn('Email delivery failed, saved locally:', error);
      setEmailSaved(true);
      setEmailDeliveryMode('local');
      setHasAnyEmailCapture(true);
      trackEvent('email_captured_local_fallback', {
        source: 'results',
        primaryType: profile?.primaryType || null,
        message: error.message,
      });
    } finally {
      setIsEmailSending(false);
    }
  };
  
  return (
    <ResultsContainer id="main-content">
      <Seo
        title={primaryFounderType ? `${primaryFounderType.title.replace('The ', '')} founder type` : 'Results'}
        description={primaryFounderType?.tagline || 'Your founder type with a practical week plan, stress pattern, and check-in to stay on track.'}
        path={location.pathname}
        imagePath="/favicon.svg"
      />
      <DnaBackground />
      <ResultsBackgroundElements />
      {profile && profile.percentages && (
        <FloatingDnaMarkers profilePercentages={profile.percentages} />
      )}
      <SiteHeader />
      <ResultsContent>
        {!storageAvailable && <StorageBlockedNotice />}
        <TypeIdentityHero
          profile={profile}
          primaryType={primaryFounderType}
          secondaryType={secondaryFounderType}
        />

        <ResultsTabNav activeTab={activeTab} onTabChange={handleTabChange} />
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ width: '100%' }}
        >
          <ResultsTabPanel tabId="overview" activeTab={activeTab} variants={itemVariants}>
          <OperatingCard as={motion.section} variants={itemVariants} aria-labelledby="profile-breakdown-heading">
            <SectionTitle id="profile-breakdown-heading" as="h2">
              Your type breakdown
            </SectionTitle>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Every founder has a primary mode and often a secondary influence. Percentages show
              what share of your weighted answers pointed to each type, not a clinical match score.
            </p>
            <ScoreCardsContainer>
              {profile && profile.primaryType && founderTypes[profile.primaryType] && (
                <FounderTypeCard 
                  type={founderTypes[profile.primaryType]}
                  score={profile.percentages[profile.primaryType] || 0} 
                  isPrimary={true}
                  showDetails={true} 
                />
              )}
              
              {profile && profile.secondaryType && founderTypes[profile.secondaryType] && (
                <FounderTypeCard 
                  type={founderTypes[profile.secondaryType]}
                  score={profile.percentages[profile.secondaryType] || 0} 
                  isSecondary={true}
                  showDetails={true} 
                />
              )}
            </ScoreCardsContainer>

            <SmallMeta>
              {createdAt ? `Saved ${new Date(createdAt).toLocaleDateString()}. ` : ''}
              Tip: your blend matters. Your top two types are usually your default settings.
            </SmallMeta>
          </OperatingCard>

          <OperatingCard as={motion.section} variants={itemVariants} aria-labelledby="why-heading">
            <SectionTitle id="why-heading" as="h2">
              Why you got this result
            </SectionTitle>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              We score by counting which founder modes your answers most often pointed to, with extra
              weight on high-signal questions (pressure, decisions, risk).
              Your top type is <strong>{formatFounderTitle(primaryFounderType?.title)}</strong> ({profile.percentages?.[profile.primaryType] || 0}% of answers).
              {profile.secondaryType && founderTypes[profile.secondaryType]
                ? ` Your second is ${formatFounderTitle(founderTypes[profile.secondaryType].title)} (${profile.percentages?.[profile.secondaryType] || 0}%).`
                : ''}
            </p>
            <ResultDriversPanel profile={profile} limit={5} />
          </OperatingCard>

          {/* Archetype layer */}
          {profile?.archetypes?.primary && (
            <ArchetypeCard as={motion.section} variants={itemVariants} aria-labelledby="archetype-heading">
              <SectionTitle id="archetype-heading" as="h2">
                Your archetypes
              </SectionTitle>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Archetypes are readable summaries derived from your blend across the 6 core modes. They help you apply your results in real situations.
              </p>
              <List>
                <li>
                  <strong>Primary</strong>: {profile.archetypes.primary.title} ({profile.archetypes.primary.score}%)
                </li>
                {profile?.archetypes?.secondary && (
                  <li>
                    <strong>Secondary</strong>: {profile.archetypes.secondary.title} ({profile.archetypes.secondary.score}%)
                  </li>
                )}
                <li>
                  <strong>Archetype confidence</strong>: {profile?.archetypes?.confidence?.label || 'low'} (margin {profile?.archetypes?.confidence?.margin ?? 0})
                </li>
              </List>
              <SmallMeta>
                Primary description: {profile.archetypes.primary.description}
              </SmallMeta>
              {profile.archetypes.primary.focus && (
                <SmallMeta>
                  Suggested focus: {profile.archetypes.primary.focus}
                </SmallMeta>
              )}
              {profile.archetypes.primary.watchOut && (
                <SmallMeta>
                  Watch out: {profile.archetypes.primary.watchOut}
                </SmallMeta>
              )}
            </ArchetypeCard>
          )}

          <DeepDiveSection 
            profile={profile} 
            founderType={primaryFounderType} 
            as={motion.div} 
            variants={itemVariants} 
          />

          <ProfileBreakdownSection as={motion.div} variants={itemVariants}>
            <ProfileBreakdownTitle as="h2">Full type spectrum</ProfileBreakdownTitle>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              How you score across all six founder types: your versatility and growth edges.
            </p>
            <ScoreCardsContainer>
              {Object.keys(founderTypes)
                .filter(type => type !== profile.primaryType && type !== profile.secondaryType)
                .map(type => 
                  founderTypes[type] ? (
                    <FounderTypeCard 
                      key={type}
                      type={founderTypes[type]} 
                      score={profile.percentages[type] || 0} 
                    />
                  ) : null
                )}
            </ScoreCardsContainer>
          </ProfileBreakdownSection>

          <PremiumContent
            title="Advanced founder insights"
            description="Deeper analysis of your type: strengths, blind spots, and strategies tailored to your blend."
            features={[
              'Psychological profile of your founder type',
              'Strategies to leverage your strengths',
              'Recommendations for overcoming challenges',
              'Ideal team composition for your DNA',
              'Development resources matched to you',
            ]}
          >
            {profile && primaryFounderType && (
                <div>
                  <p>
                  As a <strong>{formatFounderTitle(primaryFounderType?.title)}</strong>, your greatest strengths include exceptional {primaryFounderType?.strengths[0].toLowerCase()} and {primaryFounderType?.strengths[1].toLowerCase()}. 
                  These qualities make you particularly effective at leading through {primaryFounderType?.title === 'The Visionary' ? 'inspiration and big-picture thinking' : 
                  primaryFounderType?.title === 'The Executor' ? 'disciplined action and reliable follow-through' : 
                  primaryFounderType?.title === 'The Innovator' ? 'creative problem-solving and unconventional approaches' : 
                  primaryFounderType?.title === 'The Analyst' ? 'data-driven decisions and logical reasoning' : 
                  primaryFounderType?.title === 'The Strategist' ? 'systematic planning and anticipating challenges' : 
                  'relationship building and creating strong networks'}.
                </p>
                  <p>
                  Your unique blend of {formatFounderTitle(primaryFounderType?.title).replace('The ', '').toLowerCase()} and {profile.secondaryType && founderTypes[profile.secondaryType] ? formatFounderTitle(founderTypes[profile.secondaryType].title).replace('The ', '').toLowerCase() : ''} traits gives you a distinct advantage in {
                    (primaryFounderType?.title === 'The Visionary' && profile.secondaryType && founderTypes[profile.secondaryType] && founderTypes[profile.secondaryType].title === 'The Executor') ? 'not only seeing the future but making it happen' :
                    (primaryFounderType?.title === 'The Visionary' && profile.secondaryType && founderTypes[profile.secondaryType] && founderTypes[profile.secondaryType].title === 'The Innovator') ? 'creating truly revolutionary visions and approaches' :
                    (primaryFounderType?.title === 'The Executor' && profile.secondaryType && founderTypes[profile.secondaryType] && founderTypes[profile.secondaryType].title === 'The Strategist') ? 'implementing plans with both efficiency and foresight' :
                    (primaryFounderType?.title === 'The Innovator' && profile.secondaryType && founderTypes[profile.secondaryType] && founderTypes[profile.secondaryType].title === 'The Analyst') ? 'generating creative ideas that are also practical and viable' :
                    'balancing multiple founder strengths in a complementary way'
                  }.
                </p>
                  <SectionTitle>Personalized development plan</SectionTitle>
                  <ol>
                    <li>Focus on developing your {primaryFounderType?.challenges[0].replace('May ', '').replace('Can ', '').replace('Sometimes ', '')}</li>
                    <li>Partner with someone strong in {profile.compatibleTypes[0]} capabilities to balance your approach</li>
                    <li>Practice techniques for {primaryFounderType?.challenges[1].replace('May ', '').replace('Can ', '').replace('Sometimes ', '')}</li>
                    <li>Allocate dedicated time for {profile.secondaryType && founderTypes[profile.secondaryType] ? founderTypes[profile.secondaryType].strengths[0].toLowerCase() : ''}</li>
                    <li>Seek mentorship from experienced founders with {profile.compatibleTypes[1]} traits</li>
                  </ol>
                </div>
              )}
            </PremiumContent>

          <DetailedCard as={motion.div} variants={itemVariants}>
            <SectionTitle as="h2">Strategic development plan</SectionTitle>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              A long-term lens on leveraging your strengths and addressing blind spots.
            </p>
            <ActionPlan>
              {profile.primaryType && founderTypes[profile.primaryType] ? 
                founderTypes[profile.primaryType].actionPlan : 
                "Focus on your strengths while developing strategies to address your challenges."}
              {profile.secondaryType && founderTypes[profile.secondaryType] && (
                <>
                  <br /><br />
                  <strong>With your secondary {formatFounderTitle(founderTypes[profile.secondaryType].title)} traits:</strong><br />
                  {founderTypes[profile.secondaryType].actionPlan}
                </>
              )}
            </ActionPlan>
          </DetailedCard>

          <ConfidenceCard as={motion.section} variants={itemVariants} aria-labelledby="confidence-heading">
            <SectionTitle id="confidence-heading" as="h2">
              Result confidence
            </SectionTitle>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Confidence reflects how far ahead your top type is versus your second-best type.
            </p>
            <List>
              <li>
                <strong>Confidence</strong>: {profile?.confidence?.label || 'low'} (margin {profile?.confidence?.marginPercent ?? 0}%)
              </li>
              <li>
                <strong>Scoring version</strong>: {profile?.scoringVersion || 1}
              </li>
            </List>
            <SmallMeta>
              Low confidence? Treat your top two types as a blend and lean on the weekly playbook.
            </SmallMeta>
          </ConfidenceCard>

          </ResultsTabPanel>

          <ResultsTabPanel tabId="week" activeTab={activeTab} variants={itemVariants}>
          <PremiumWelcomeBanner
            primaryTypeTitle={
              primaryFounderType ? formatFounderTitle(primaryFounderType.title) : null
            }
            onExplore={() => scrollToSection('week-plan-heading')}
          />

          {/* Default failure mode under stress */}
          {playbook?.stressFailureMode && (
            <OperatingCard as={motion.section} variants={itemVariants} aria-labelledby="stress-heading">
              <SectionTitle id="stress-heading" as="h2">
                Your default failure mode under stress
              </SectionTitle>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {playbook.stressFailureMode}
              </p>
              {playbook.decisionChecklist?.length > 0 && (
                <>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
                    Use this quick decision checklist:
                  </p>
                  <List>
                    {playbook.decisionChecklist.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </List>
                </>
              )}
            </OperatingCard>
          )}

          {/* (3) Your 3-step plan for this week */}
          {playbook?.threeStepWeekPlan?.length > 0 && (
            <OperatingCard as={motion.section} variants={itemVariants} aria-labelledby="week-plan-heading">
              <SectionTitle id="week-plan-heading" as="h2">
                Your 3-step plan for this week
              </SectionTitle>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Don’t try to “become” another type. Install 3 behaviors that make your strengths reliable under pressure.
              </p>
              <OrderedList>
                {playbook.threeStepWeekPlan.slice(0, 3).map(item => (
                  <li key={item.title} style={{ marginTop: '0.75rem' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{item.title}</strong>
                    {item.steps?.length > 0 && (
                      <List>
                        {item.steps.map(step => (
                          <li key={step}>{step}</li>
                        ))}
                      </List>
                    )}
                  </li>
                ))}
              </OrderedList>
            </OperatingCard>
          )}
          
          <MeetingScriptsSection playbook={playbook} profile={profile} />

          <CheckInCard as={motion.section} variants={itemVariants} aria-labelledby="check-in-heading">
            <SectionTitle id="check-in-heading" as="h2">
              Weekly check-in
            </SectionTitle>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Three quick questions. One focus for the next 7 days.
            </p>
            <CheckInRow>
              <CheckInMeta>
                <div>
                  {latestCheckIn?.createdAt
                    ? `Last check-in: ${new Date(latestCheckIn.createdAt).toLocaleDateString()}`
                    : 'No check-ins yet'}
                </div>
                <div>
                  Next check-in: {new Date(nextCheckInDate).toLocaleDateString()}
                </div>
                {reminderDownloaded && (
                  <div style={{ color: 'var(--type-accent, var(--primary-color))' }}>
                    Reminder downloaded
                  </div>
                )}
              </CheckInMeta>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <ReminderButton
                  type="button"
                  onClick={handleDownloadReminder}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Add calendar reminder
                </ReminderButton>
                <CheckInButton
                  type="button"
                  onClick={() => navigate('/check-in')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Start check-in
                </CheckInButton>
              </div>
            </CheckInRow>
          </CheckInCard>

          </ResultsTabPanel>

          <ResultsTabPanel tabId="team" activeTab={activeTab} variants={itemVariants}>
          <CofounderCompareTeaser shareUrl={shareUrl} variants={itemVariants} />

          {profile.compatibleTypes && profile.compatibleTypes.length > 0 ? (
            <CompatibilitySection as={motion.div} variants={itemVariants}>
              <CompatibilityTitle as="h2">Complementary founder modes</CompatibilityTitle>
              <CompatibilityDescription>
                {RESULTS.teamIntro} These modes often balance how you lead, decide, and execute.
              </CompatibilityDescription>
              <ScoreCardsContainer>
                {profile.compatibleTypes
                  .filter(type => founderTypes[type.toLowerCase()])
                  .map(type => (
                    <FounderTypeCard 
                      key={type}
                      type={founderTypes[type.toLowerCase()]} 
                      score={100} 
                    />
                  ))}
              </ScoreCardsContainer>
            </CompatibilitySection>
          ) : (
            <OperatingCard as={motion.section} variants={itemVariants}>
              <SectionTitle as="h2">Co-founder fit</SectionTitle>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Explore compatible founder types on your type profile pages, or share your results to compare with a co-founder.
              </p>
            </OperatingCard>
          )}

          </ResultsTabPanel>

          <ResultsTabPanel tabId="tools" activeTab={activeTab} variants={itemVariants}>
          <p
            style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              margin: '0 0 1.25rem',
              width: '100%',
              maxWidth: '720px',
            }}
          >
            {RESULTS.deliverableLead}
          </p>
          <ResultsToolsGrid
            onExportPdf={handleExportPdf}
            onScrollToEmail={() => scrollToSection('email-capture-heading')}
            onScrollToShare={() => scrollToSection('share-section')}
            onScrollToBackup={() => scrollToSection('backup-heading')}
          />

          <motion.div id="share-section" variants={itemVariants}>
            <ShareResults
              profileType={profile.primaryType && founderTypes[profile.primaryType]
                ? formatFounderTitle(founderTypes[profile.primaryType].title)
                : "Founder"}
              primaryTitle={profile.primaryType && founderTypes[profile.primaryType]
                ? formatFounderTitle(founderTypes[profile.primaryType].title)
                : null}
              primaryPercent={typeof profile?.percentages?.[profile.primaryType] === 'number'
                ? profile.percentages[profile.primaryType]
                : null}
              secondaryTitle={profile.secondaryType && founderTypes[profile.secondaryType]
                ? formatFounderTitle(founderTypes[profile.secondaryType].title)
                : null}
              secondaryPercent={profile.secondaryType && typeof profile?.percentages?.[profile.secondaryType] === 'number'
                ? profile.percentages[profile.secondaryType]
                : null}
              oneLineInsight={playbook?.threeStepWeekPlan?.[0]?.title || null}
              shareUrl={shareUrl}
              primaryTypeId={profile.primaryType}
              secondaryTypeId={profile.secondaryType}
            />
          </motion.div>

          <ResultsEmailSection
            email={email}
            setEmail={setEmail}
            emailConsent={emailConsent}
            setEmailConsent={setEmailConsent}
            emailError={emailError}
            setEmailError={setEmailError}
            emailSaved={emailSaved}
            setEmailSaved={setEmailSaved}
            isEmailSending={isEmailSending}
            emailDeliveryMode={emailDeliveryMode}
            hasAnyEmailCapture={hasAnyEmailCapture}
            onSubmit={handleSaveEmail}
            variants={itemVariants}
          />

          <ResultsHistorySection
            historyRuns={historyRuns}
            selectedRunId={selectedRunId}
            onViewRun={handleViewRun}
            onNameChange={handleHistoryNameChange}
            onNameCommit={handleHistoryNameCommit}
            variants={itemVariants}
          />

          <ResultsBackupSection
            pendingBackup={pendingBackup}
            pendingBackupSummary={pendingBackupSummary}
            backupError={backupError}
            backupMessage={backupMessage}
            onExport={handleExportBackup}
            onChooseImport={handleChooseImportFile}
            onImportSelected={handleImportFileSelected}
            onRestore={handleRestoreBackup}
            onDeleteAll={handleDeleteAllLocalData}
            variants={itemVariants}
          />
          
          <motion.div
            variants={itemVariants}
            style={{ textAlign: 'center', marginTop: '2rem' }}
          >
            <RetakeButton 
              onClick={handleRetakeQuiz}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Retake the test
            </RetakeButton>
          </motion.div>

          </ResultsTabPanel>
        </motion.div>
        <SiteFooter />
      </ResultsContent>
    </ResultsContainer>
  );
};

export default Results; 